import Pusher from 'pusher-js'
import axios from 'axios'
import swal from 'sweetalert2/dist/sweetalert2'
import $ from 'jquery'
import { trans } from '@/munio/i18n'

//
// Colors (hex values for ECharts compatibility - oklch not supported)
//

export const chartColors = [
  '#2563eb', // blue-600
  '#16a34a', // green-600
  '#eab308', // yellow-500
  '#dc2626', // red-600
  '#c026d3', // fuchsia-600
  '#0891b2', // cyan-600
  '#f97316', // orange-500
  '#65a30d', // lime-600
  '#0284c7', // sky-600
  '#e11d48', // rose-600
  '#0d9488', // teal-600
  '#6366f1', // indigo-500
  '#059669', // emerald-600
  '#78716c', // stone-500
  '#db2777', // pink-600
  '#64748b', // slate-500
]

Munio.chartColors = chartColors

export function ecoProduct(product) {
  return Munio.config.ecoonline.products.find(
    (ecoProduct) => ecoProduct.key === product,
  )
}

/**
 * Websocket
 */
let pusher

export const Websocket = function () {
  if (pusher) return pusher

  const { key, options } = Munio.config.websocket

  pusher = new Pusher(key, options)

  pusher.private = function (channel) {
    return pusher.subscribe('private-' + channel)
  }

  pusher.presence = function (channel) {
    return pusher.subscribe('presence-' + channel)
  }

  axios.interceptors.request.use((config) => {
    if (pusher.connection.socket_id) {
      config.headers['X-Socket-Id'] = pusher.connection.socket_id
    }

    return config
  })

  if (typeof $.ajax !== 'undefined') {
    $.ajaxPrefilter((_options, _originalOptions, xhr) => {
      if (pusher.connection.socket_id) {
        xhr.setRequestHeader('X-Socket-Id', pusher.connection.socket_id)
      }
    })
  }

  if (isDevMode) {
    pusher.connection.bind_global(function () {
      console.debug('[PUSHER]', ...arguments)
    })
  }

  return pusher
}

/**
 * Show a flash message
 *
 * @type {{message, success, info}}
 * @link https://limonte.github.io/sweetalert2/
 */
Munio.Flash = (function () {
  const parseArguments = function (title, text, options = {}) {
    if (typeof title !== 'string' && title !== null) {
      options = title
      title = text = null
    }

    if (typeof text !== 'string' && text !== null) {
      options = text
      text = null
    }

    if (!title) {
      title = trans('Are you sure?')
    }

    return { title, text, options }
  }

  const message = function (title, text, options = {}, defaultOptions = {}) {
    ;({ title, text, options } = parseArguments(title, text, options))

    const globalOptions = {
      reverseButtons: true,
      buttonsStyling: false,
      allowEnterKey: false,
      cancelButtonClass: 'mdl-button mdl-js-button',
      cancelButtonText: trans('Cancel'),
      confirmButtonClass:
        'mdl-button mdl-js-button mdl-button--raised mdl-button--primary',
      confirmButtonText: trans('OK'),
    }

    return swal(
      Object.assign({}, globalOptions, defaultOptions, options, {
        html: text || undefined,
        title,
      }),
    )
  }

  return {
    message,
    success: (title = trans('Success'), text = undefined, options = {}) => {
      return message(title, text, options, {
        type: 'success',
        showConfirmButton: false,
        timer: 1800,
      })
    },
    info: (title, text, options = {}) => {
      console.info(title, text)
      return message(title, text, options, { type: 'info' })
    },
    error: (title, text, options = {}) => {
      console.error(title, text)
      return message(title, text, options, { type: 'error' })
    },
    warning: (title, text, options = {}) => {
      console.warn(title, text)
      return message(title, text, options, { type: 'warning' })
    },
    question: (title, text, options = {}) => {
      return message(title, text, options, { type: 'question' })
    },
  }
})()

/**
 * Show a confirmation dialog
 *
 * @param {string?} [title]
 * @param {string?} [text]
 * @param {object?} [options]
 * @returns {Promise<number|string|boolean>}
 */
Munio.confirm = async function (
  title = undefined,
  text = undefined,
  options = {},
) {
  const response = await Munio.Flash.message(title, text, options, {
    type: 'question',
    showCancelButton: true,
    confirmButtonText: trans('Confirm'),
  })

  if (response.dismiss) {
    return false
  }

  return response.value ?? false
}

/**
 * Show an alert dialog
 *
 * @param title
 * @param text
 * @param options
 * @returns {Promise<boolean>}
 */
Munio.alert = async function (title, text, options = {}) {
  return (await Munio.Flash.info(title, text, options)).value || false
}

/**
 * Show a prompt dialog
 *
 * @param title
 * @param text
 * @param options
 * @returns {Promise<any>}
 */
Munio.prompt = async function (title, text, options = {}) {
  const defaultOptions = {
    type: 'question',
    input: 'text',
    showCancelButton: true,
  }

  return (
    await Munio.Flash.message(
      title,
      text,
      Object.assign({}, defaultOptions, options),
    )
  ).value
}

/**
 * @param {string} id
 * @param {object} data
 * @param {Function} onChange
 * @returns {Promise<Api12>}
 */
Munio.Scorm12 = async function (id, data, onChange) {
  const Api = (await import('./scorm/Api12')).default
  const scorm = new Api(id, data)
  scorm.onChange(onChange)
  return scorm
}

/**
 * @param {string} id
 * @param {object} data
 * @param {Function} onChange
 * @returns {Promise<Api2004>}
 */
Munio.Scorm2004 = async function (id, data, onChange) {
  const Api = new (await import('./scorm/Api2004')).default()
  const scorm = new Api(id, data)
  scorm.onChange(onChange)
  return scorm
}

export default Munio
